import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DragDropFileUpload from './DragDropFileUpload';
import Modal from './Modal';
import StandardPagination from './StandardPagination';
import { createApiUrl } from '../utils/apiConfig';
import './LogoUpload.css';

const LogoUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingLogos, setExistingLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logoToDelete, setLogoToDelete] = useState(null);
  
  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [logosPerPage, setLogosPerPage] = useState(12);

  useEffect(() => {
    fetchExistingLogos();
  }, []);

  const fetchExistingLogos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(createApiUrl('/api/logos'), {
        withCredentials: true,
      });
      setExistingLogos(response.data.logos || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching existing logos:', error);
      setError('Failed to load existing logos');
      setLoading(false);
    }
  };

  const _handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const validFiles = [];
      const errors = [];

      files.forEach(file => {
        // Validate file type
        const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
          errors.push(`${file.name}: Invalid file type. Only SVG, PNG, and JPEG files are allowed.`);
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          errors.push(`${file.name}: File size must be less than 5MB`);
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        setError(errors.join(' '));
      } else {
        setError('');
      }

      setSelectedFiles(validFiles);
      setSuccess('');
    }
  };

  // File handlers for drag-drop component
  const handleLogoSelect = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      // Validate file type
      const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type. Only SVG, PNG, and JPEG files are allowed.`);
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 5MB`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(' '));
    } else {
      setError('');
    }

    setSelectedFiles(validFiles);
  };

  const handleLogoRemove = () => {
    setSelectedFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('logos', file);
      });

      // Do not set Content-Type manually — axios must add the multipart boundary
      const response = await axios.post(createApiUrl('/api/logos/upload'), formData, {
        withCredentials: true,
      });

      const successMessage = response.data.message || `${selectedFiles.length} logo(s) uploaded successfully!`;
      setSuccess(successMessage);
      
      if (response.data.errors && response.data.errors.length > 0) {
        setError(response.data.errors.join(' '));
      }
      
      setSelectedFiles([]);
      
      // Refresh the list of existing logos
      await fetchExistingLogos();

    } catch (error) {
      console.error('Upload error:', error);
      const data = error.response?.data;
      const detail = Array.isArray(data?.errors) && data.errors.length
        ? ` ${data.errors.join(' ')}`
        : '';
      setError((data?.message || 'Failed to upload logos') + detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = (logo) => {
    setLogoToDelete(logo);
    setShowDeleteModal(true);
  };

  const confirmDeleteLogo = async () => {
    if (!logoToDelete) return;

    try {
      // Send the logo name (display name) instead of filename
      await axios.delete(
        createApiUrl(`/api/logos/${encodeURIComponent(logoToDelete.name)}`),
        { withCredentials: true }
      );

      setSuccess('Logo deleted successfully!');
      setShowDeleteModal(false);
      setLogoToDelete(null);
      
      // Refresh the list of existing logos
      await fetchExistingLogos();

    } catch (error) {
      console.error('Delete error:', error);
      setError(error.response?.data?.message || 'Failed to delete logo');
    }
  };

  const cancelDeleteLogo = () => {
    setShowDeleteModal(false);
    setLogoToDelete(null);
  };

  // Search and pagination functions
  const getFilteredLogos = () => {
    if (!searchQuery) return existingLogos;
    return existingLogos.filter(logo => 
      logo.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      logo.alt?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getPaginatedLogos = () => {
    const filtered = getFilteredLogos();
    const startIndex = (currentPage - 1) * logosPerPage;
    const endIndex = startIndex + logosPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const _handleClearFile = () => {
    setSelectedFiles([]);
    setError('');
    setSuccess('');
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: '#eb6b00',
    fontFamily: 'super-boys, sans-serif'
  };

  const alertStyle = {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: 'none'
  };

  const alertDangerStyle = {
    ...alertStyle,
    backgroundColor: '#dc3545',
    color: 'white'
  };

  const alertSuccessStyle = {
    ...alertStyle,
    backgroundColor: '#28a745',
    color: 'white'
  };

  const uploadSectionStyle = {
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  };

  const formGroupStyle = {
    marginBottom: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500'
  };

  const _inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '1rem'
  };

  const _helpTextStyle = {
    fontSize: '0.875rem',
    color: '#6c757d',
    marginTop: '0.25rem'
  };

  const fileInfoStyle = {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '4px'
  };

  const buttonStyle = {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#7f14c7',
    color: '#eb6b00'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#dc3545',
    color: 'white',
    fontSize: '0.875rem',
    padding: '0.25rem 0.5rem'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6c757d',
    color: 'white',
    marginLeft: '0.5rem'
  };

  const disabledButtonStyle = {
    ...primaryButtonStyle,
    opacity: 0.6,
    cursor: 'not-allowed'
  };

  const logosGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px',
    marginTop: '1rem'
  };

  const logoCardStyle = {
    border: '2px solid #ffa300',
    borderRadius: '5px',
    backgroundColor: '#a93dea',
    overflow: 'hidden',
    transition: 'all 0.3s ease'
  };

  const _logoCardHoverStyle = {
    ...logoCardStyle,
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 20px rgba(235, 107, 0, 0.3)'
  };

  const logoPreviewStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: '0.5rem'
  };

  const logoImageStyle = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain'
  };

  const logoCardBodyStyle = {
    padding: '0.3rem',
    textAlign: 'center'
  };

  const logoNameStyle = {
    color: '#eb6b00',
    fontFamily: 'super-boys, sans-serif',
    fontSize: '1.1rem',
    fontWeight: '400',
    marginBottom: '0.2rem',
    lineHeight: '1.2',
    textAlign: 'center'
  };

  const existingLogosTitleStyle = {
    color: '#eb6b00',
    fontFamily: 'super-boys, sans-serif',
    fontWeight: '600',
    fontSize: '1.5rem',
    marginBottom: '1rem'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Partner Logo Management</h1>
      
      {error && <div style={alertDangerStyle}>{error}</div>}
      {success && <div style={alertSuccessStyle}>{success}</div>}
      
      <div style={uploadSectionStyle}>
        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Partner Logo:</label>
            <DragDropFileUpload
              onFileSelected={handleLogoSelect}
              onFileRemoved={handleLogoRemove}
              selectedFile={selectedFiles}
              accept=".svg,.png,.jpg,.jpeg"
              maxFileSize={5 * 1024 * 1024} // 5MB
              supportedTypes={['.svg', '.png', '.jpg', '.jpeg']}
              placeholder="Click to upload logos or drag and drop multiple files"
              description="Supports: SVG, PNG, JPG, JPEG (up to 10 files)"
              multiple={true}
            />
          </div>

          {selectedFiles && selectedFiles.length > 0 && (
            <div style={fileInfoStyle}>
              <h6>Selected Files ({selectedFiles.length}):</h6>
              {selectedFiles.map((file, index) => (
                <div key={index} style={{ marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                  <p style={{ marginBottom: '0.25rem' }}><strong>Name:</strong> {file.name}</p>
                  <p style={{ marginBottom: '0.25rem' }}><strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p style={{ marginBottom: '0' }}><strong>Type:</strong> {file.type}</p>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '0.5rem' }}>
            <button 
              type="submit" 
              disabled={!selectedFiles || selectedFiles.length === 0 || uploading}
              style={(!selectedFiles || selectedFiles.length === 0 || uploading) ? disabledButtonStyle : primaryButtonStyle}
            >
              {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? selectedFiles.length : ''} Logo${selectedFiles.length > 1 ? 's' : ''}`}
            </button>
            {selectedFiles && selectedFiles.length > 0 && (
              <button 
                type="button"
                onClick={handleLogoRemove}
                style={secondaryButtonStyle}
              >
                Clear Files
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <h3 style={existingLogosTitleStyle}>Existing Partner Logos</h3>
        
        {loading ? (
          <p>Loading existing logos...</p>
        ) : existingLogos.length === 0 ? (
          <p>No logos uploaded yet.</p>
        ) : (
          <>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <form onSubmit={handleSearch} className="form-inline d-flex">
                <input
                  className="form-control mr-2"
                  type="search"
                  name="search"
                  placeholder="Search logos..."
                  aria-label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                  style={{ 
                    width: '300px',
                    height: '38px',
                    fontSize: '14px'
                  }}
                />
                <button 
                  className="btn" 
                  type="submit"
                  style={{ 
                    height: '38px',
                    fontSize: '14px',
                    padding: '6px 12px',
                    backgroundColor: 'var(--darkPurple)',
                    color: 'var(--darkOrange)',
                    border: '1px solid var(--darkPurple)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--darkOrange)';
                    e.target.style.color = 'var(--darkPurple)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--darkPurple)';
                    e.target.style.color = 'var(--darkOrange)';
                  }}
                >
                  Search
                </button>
              </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="logosPerPage" className="form-label me-2 mb-0">Logos per page:</label>
                  <select 
                    id="logosPerPage"
                    className="form-select"
                    value={logosPerPage}
                    onChange={(e) => setLogosPerPage(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((currentPage - 1) * logosPerPage) + 1} to {Math.min(currentPage * logosPerPage, getFilteredLogos().length)} of {getFilteredLogos().length} logos
                </small>
              </div>
            </div>

            <div style={logosGridStyle}>
              {getPaginatedLogos().map((logo, index) => (
                <div 
                  key={index} 
                  style={logoCardStyle}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(235, 107, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <div style={logoPreviewStyle}>
                    <img 
                      src={logo.logo} 
                      alt={logo.alt}
                      style={logoImageStyle}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{ display: 'none', color: '#ffa300', fontSize: '0.9rem', textAlign: 'center', padding: '1rem' }}>
                      {logo.name}
                    </div>
                  </div>
                  <div style={logoCardBodyStyle}>
                    <div style={logoNameStyle}>{logo.name}</div>
                    <button 
                      style={dangerButtonStyle}
                      onClick={() => handleDeleteLogo(logo)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {getFilteredLogos().length > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={Math.ceil(getFilteredLogos().length / logosPerPage)}
                onPageChange={handlePageChange}
                itemsPerPage={logosPerPage}
                onItemsPerPageChange={setLogosPerPage}
                totalItems={getFilteredLogos().length}
                itemsPerPageLabel="Logos per page:"
                pageInfoLabel="Showing"
                showItemsPerPage={false}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={cancelDeleteLogo} className="delete-confirmation-modal">
        <div className="modal-header">
          <h3>Confirm Logo Deletion</h3>
          <button className="close-btn" onClick={cancelDeleteLogo}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>
            Are you sure you want to delete the logo 
            <strong> "{logoToDelete?.name}"</strong>?
          </p>
          <p className="text-muted">
            This logo will be permanently removed from the system.
          </p>
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={cancelDeleteLogo}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger"
            onClick={confirmDeleteLogo}
          >
            <i className="fas fa-trash me-2"></i>
            Delete Logo
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LogoUpload; 